<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import CmkCollapsible from '@/components/CmkCollapsible'
import { getWizardContext } from '@/components/CmkWizard/utils.ts'

export interface CmkWizardStepProps {
  index: number
  isCompleted: () => boolean
}
const context = getWizardContext()

const props = defineProps<CmkWizardStepProps>()

const contentRef = ref<HTMLElement | null>(null)
const isActive = computed(() => context.isSelected(props.index))

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

watch(isActive, async (active) => {
  if (active) {
    await nextTick()
    const target =
      contentRef.value?.querySelector<HTMLElement>('.cmk-link-card') ??
      contentRef.value?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
    target?.focus()
  }
})

function onClickGoTo() {
  if (context.mode() === 'guided' && props.isCompleted()) {
    context.navigation.goto(props.index)
  }
}
</script>

<template>
  <li
    class="cmk-wizard-step__row"
    :class="{
      'cmk-wizard-step--active': context.isSelected(index) && context.mode() !== 'overview',
      'cmk-wizard-step--complete': isCompleted() && context.mode() !== 'overview',
      'cmk-wizard-step--overview': context.mode() === 'overview',
      'cmk-wizard-step--locked': context.locked()
    }"
    @click="onClickGoTo"
  >
    <div class="cmk-wizard-step__slots-outer">
      <slot name="header"></slot>
      <div v-if="context.mode() === 'overview'" ref="contentRef">
        <slot name="content" />
        <div class="cmk-wizard-step__actions" @click.stop>
          <slot name="actions" />
        </div>
      </div>
      <CmkCollapsible
        v-else
        :open="context.isSelected(props.index)"
        class="cmk-wizard-step__slots-inner"
      >
        <div ref="contentRef" class="cmk-wizard-step__content">
          <slot name="content"></slot>
        </div>
        <div class="cmk-wizard-step__actions" @click.stop>
          <slot name="actions"></slot>
        </div>
      </CmkCollapsible>
    </div>
  </li>
</template>

<style scoped>
.cmk-wizard-step__slots-outer {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-4);
  width: calc(100% - 40px);
}

.cmk-wizard-step__row {
  position: relative;
  display: flex;
  gap: var(--dimension-6);

  --wizard-step-number-badge-width: var(--dimension-8);
  --wizard-progress-bar-width: var(--dimension-2);

  &:not(:last-child) {
    padding-bottom: 1rem;
  }

  /* Step number badge */
  &::before {
    counter-increment: stage-index;
    content: counter(stage-index);
    align-content: center;
    text-align: center;
    font-size: var(--font-size-normal);
    font-weight: bold;
    position: relative;
    z-index: 1;
    flex: 0 0 var(--wizard-step-number-badge-width);
    height: var(--wizard-step-number-badge-width);
    border-radius: 50%;
    color: var(--wizard-progress-bar-background-badge-color);
    border: var(--wizard-progress-bar-width) solid var(--wizard-progress-bar-background-color);
    background-color: transparent;
    box-sizing: border-box;
  }

  /* Connecting line between badges */
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: calc(var(--wizard-step-number-badge-width) / 2 - 1px);
    top: var(--wizard-step-number-badge-width);
    bottom: 0;
    width: var(--wizard-progress-bar-width);
    background-color: var(--wizard-progress-bar-background-color);
  }

  &.cmk-wizard-step--active::before,
  &.cmk-wizard-step--complete::before {
    background-color: var(--success-dimmed);
    border-color: var(--success-dimmed);
  }

  &.cmk-wizard-step--active::after {
    background: linear-gradient(
      to bottom,
      var(--success-dimmed) 50px,
      var(--wizard-progress-bar-background-color) 50px
    );
  }

  &.cmk-wizard-step--complete {
    &::before {
      background-image: url('~cmk-frontend/themes/facelift/images/icon_check.svg');
      background-repeat: no-repeat;
      background-position: center;
      content: '';
    }

    &::after {
      background-color: var(--success-dimmed);
    }
  }

  &.cmk-wizard-step--complete:not(
      .cmk-wizard-step--active,
      .cmk-wizard-step--overview,
      .cmk-wizard-step--locked
    ) {
    &::before {
      cursor: pointer;
    }

    .cmk-wizard-step__slots-outer {
      cursor: pointer;
    }
  }
}

.cmk-wizard-step__slots-inner {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-4);
  width: 100%;
  align-items: flex-start;
}

.cmk-wizard-step__content {
  width: 100%;
}

.cmk-wizard-step__actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--dimension-4);
}

.cmk-wizard-step--overview .cmk-wizard-step__actions {
  margin-top: var(--dimension-4);
}
</style>
